import React from 'react';
import { useAppDispatch, useAppSelector } from '@state/store';
import { updateBoundaryConditions } from '@state/slices/studySlice';
import type { BoundaryConditionType } from '@contracts/index';

const BOUNDARY_TYPE_OPTIONS: { value: BoundaryConditionType; label: string }[] = [
  { value: 'velocity_inlet', label: 'Velocity boundary' },
  { value: 'pressure_outlet', label: 'Pressure / outflow boundary' },
  { value: 'no_slip_wall', label: 'No-slip' },
  { value: 'slip_wall', label: 'Slip / Symmetry' },
  { value: 'symmetry', label: 'Symmetry' },
  { value: 'open', label: 'Open boundary' }
];

/**
 * BoundariesInspector (Gate 1 Foundation)
 *
 * Exposes macroscopic physical boundary condition assignments:
 * - Inlet (Velocity boundary)
 * - Outlet (Pressure/outflow boundary)
 * - Ground (No-slip)
 * - Buildings (No-slip)
 * - Side boundaries (Slip / Symmetry / Open)
 * - Top boundary (Slip / Symmetry / Open)
 *
 * Zero solver syntax, zero numerical turbulence parameters, zero wall functions.
 */
export const BoundariesInspector: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeScenarioId = useAppSelector((state) => state.study.study.activeScenarioId);
  const activeScenario = useAppSelector(
    (state) => state.study.study.scenarios[activeScenarioId]
  );
  const bc = activeScenario?.cfdSetup?.boundaryConditions;

  const boundaries = [
    {
      key: 'inlet' as const,
      name: 'Inlet',
      description: 'Incoming flow boundary at upwind domain face',
      value: bc?.inlet ?? 'velocity_inlet'
    },
    {
      key: 'outlet' as const,
      name: 'Outlet',
      description: 'Outflow boundary at downwind domain face',
      value: bc?.outlet ?? 'pressure_outlet'
    },
    {
      key: 'ground' as const,
      name: 'Ground',
      description: 'Bottom surface at elevation Y = 0.0 m',
      value: bc?.ground ?? 'no_slip_wall'
    },
    {
      key: 'buildings' as const,
      name: 'Buildings',
      description: 'Solid obstacle surfaces of extruded site masses',
      value: bc?.buildings ?? 'no_slip_wall'
    },
    {
      key: 'sides' as const,
      name: 'Side boundaries',
      description: 'Lateral domain boundaries parallel to flow',
      value: bc?.sides ?? 'slip_wall'
    },
    {
      key: 'top' as const,
      name: 'Top boundary',
      description: 'Upper computational domain ceiling at Y = Height',
      value: bc?.top ?? 'slip_wall'
    }
  ];

  const handleChange = (key: keyof typeof bc, value: BoundaryConditionType) => {
    dispatch(updateBoundaryConditions({ [key]: value }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '11.5px' }}>
      {/* Header Info */}
      <div style={{ borderBottom: '1px solid rgba(47, 120, 196, 0.12)', paddingBottom: '8px' }}>
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: '9px',
            fontWeight: 700,
            color: '#2F78C4',
            letterSpacing: '0.12em',
            marginBottom: '2px'
          }}
        >
          PHYSICAL BOUNDARY SPECIFICATION
        </div>
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
          Boundary Conditions
        </div>
        <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '2px' }}>
          Scenario: <strong>{activeScenario?.name || 'Baseline'}</strong>
        </div>
      </div>

      {/* Boundary Conditions List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {boundaries.map((b) => (
          <div
            key={b.key}
            style={{
              padding: '8px 10px',
              border: '1px solid rgba(47, 120, 196, 0.16)',
              borderRadius: '4px',
              background: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, color: '#0f172a' }}>{b.name}</span>
              <span
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: '9px',
                  color: '#64748b'
                }}
              >
                {b.key.toUpperCase()}
              </span>
            </div>

            <select
              value={b.value}
              onChange={(e) => handleChange(b.key, e.target.value as BoundaryConditionType)}
              style={{
                height: '26px',
                padding: '0 6px',
                borderRadius: '3px',
                border: '1px solid rgba(47, 120, 196, 0.25)',
                background: '#FAF8F5',
                fontSize: '11px',
                color: '#1e3a8a',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              {BOUNDARY_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <span style={{ fontSize: '9.5px', color: '#64748b', lineHeight: 1.3 }}>
              {b.description}
            </span>
          </div>
        ))}
      </div>

      {/* Engine Independence & Decoupling Notice */}
      <div
        style={{
          borderLeft: '3px solid #2450A6',
          padding: '6px 10px',
          background: 'rgba(36, 80, 166, 0.05)',
          borderRadius: '0 3px 3px 0',
          fontSize: '10px',
          color: '#334155',
          lineHeight: 1.4
        }}
      >
        <div style={{ fontWeight: 600, color: '#1e3a8a', marginBottom: '2px' }}>
          Engine-Independent Boundary Contract
        </div>
        <div>
          Exposing macroscopic physical boundaries only. Solver-specific definitions (such as zeroGradient,
          nutkWallFunction, or lattice bounce-back) remain decoupled and deferred to future engine adapters.
        </div>
      </div>
    </div>
  );
};

export default BoundariesInspector;
